import { useLocation, useRouter } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FiArrowLeft, FiSearch, FiX } from "react-icons/fi";
import { HiOutlineMenu } from "react-icons/hi";
import { resolveHeaderMascot } from "@/app/layouts/headerMascot";
import { getInitialHeaderScrollY } from "@/app/layouts/headerScroll";
import { isHistoryPath } from "@/app/layouts/headerSearchVisibility";
import { SkipToMainContentLink } from "@/app/layouts/layoutAccessibility";
import icon_aimoge from "@/assets/img/icon_aimoge.webp";
import icon_aimoge_dark1 from "@/assets/img/icon_aimoge_dark1.webp";
import icon_blumoge from "@/assets/img/icon_blumoge.webp";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { SearchBar } from "@/features/catalog/components";
import { useCurrentUrlSlug } from "@/shared/hooks/useCurrentUrlSlug";
import { MIX_CATALOG_HREF, useFromMixCatalog } from "@/shared/lib/mixReturn";
import { resolveThreadBoardScope } from "@/shared/lib/threadBoardScope";
import {
  isThreadRoutePath,
  parseMixRouteThreadSlug,
} from "@/shared/lib/threadRoutePath";

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: FunctionComponent<HeaderProps> = ({
  onMenuClick,
}: HeaderProps) => {
  const location = useLocation();
  const router = useRouter();
  const fromMixCatalog = useFromMixCatalog();
  const isThreadView = isThreadRoutePath(location.pathname);
  // カタログ/リスト等はマウント全体の面(mixなら総合名)、スレページは個別板の面。
  // 対象板はrouterのpathnameから解決する(window.locationはpushState遅延で
  // 遷移直後のrenderに追いつかない。詳細はparseMixRouteThreadSlug)
  const displayName = isThreadView
    ? resolveThreadBoardScope(parseMixRouteThreadSlug(location.pathname))
        .displayName
    : BOARD_DISPLAY_NAME;
  const mascot = resolveHeaderMascot(useCurrentUrlSlug());
  const isSearchAvailable = !isThreadView && !isHistoryPath(location.pathname);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(getInitialHeaderScrollY());

  useEffect(() => {
    if (!isSearchAvailable) setIsSearchOpen(false);
  }, [isSearchAvailable]);

  // 検索バーの開閉
  const toggleSearch = useCallback(() => {
    lastScrollYRef.current = window.scrollY;
    setIsHeaderVisible(true);
    setIsSearchOpen((prev) => !prev);
  }, []);

  // スクロール方向に応じてヘッダーの表示/非表示を切り替え
  useEffect((): (() => void) => {
    const SCROLL_THRESHOLD = 5;
    const TOP_OFFSET = 10;

    const handleScroll = (): void => {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollYRef.current;

      // 検索バーが開いている間はヘッダーを隠さない
      if (isSearchOpen) {
        lastScrollYRef.current = currentScrollY;
        return;
      }

      if (currentScrollY < TOP_OFFSET) {
        setIsHeaderVisible(true);
      } else if (diff > SCROLL_THRESHOLD) {
        setIsHeaderVisible(false);
      } else if (diff < -SCROLL_THRESHOLD) {
        setIsHeaderVisible(true);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return (): void => window.removeEventListener("scroll", handleScroll);
  }, [isSearchOpen]);

  // 戻る処理：履歴があれば戻る、なければトップページへ遷移
  // (?mix付きスレを直接開いた場合のフォールバックだけは総合カタログへ)
  const handleBack = useCallback(() => {
    if (router.history.canGoBack?.()) {
      router.history.back();
    } else if (fromMixCatalog) {
      window.location.assign(MIX_CATALOG_HREF);
    } else {
      void router.navigate({ to: "/", replace: true });
    }
  }, [router, fromMixCatalog]);

  return (
    <>
      <SkipToMainContentLink className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground" />
      <header
        className={`sticky top-0 z-20 pt-[env(safe-area-inset-top)] bg-card border-b border-border transition-transform duration-300 ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <section
          aria-label={`${displayName}のヘッダー操作`}
          className={`flex items-center justify-between px-4 py-0 ${
            isSearchAvailable
              ? ""
              : "after:block after:h-11 after:w-12 after:content-['']"
          }`}
        >
          <button
            type="button"
            onClick={isThreadView ? handleBack : onMenuClick}
            className="flex h-11 w-12 items-center justify-center rounded-full transition-colors hover:bg-muted"
            aria-label={isThreadView ? "戻る" : "メニュー"}
          >
            {isThreadView ? (
              <FiArrowLeft aria-hidden="true" className="w-6 h-6" />
            ) : (
              <HiOutlineMenu aria-hidden="true" className="w-6 h-6" />
            )}
          </button>

          <section
            aria-label={`${displayName}の案内`}
            className="flex flex-col items-center"
          >
            <strong className="flex items-center text-lg font-bold text-foreground">
              <img
                src={mascot}
                alt=""
                aria-hidden="true"
                className="mr-1 h-8 w-8 object-contain"
              />
              {displayName}
            </strong>
            <nav
              aria-label="関連サービス"
              className="flex items-center gap-2 pb-1 pt-1"
            >
              <a
                href="https://vote.nijiurachan.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 items-center gap-1 px-1 text-sm text-secondary hover:text-primary transition-colors"
              >
                <img src={icon_aimoge} alt="" className="h-6 w-6" />
                投票所
              </a>
              <a
                href="https://aimoge.fanbox.cc/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 items-center gap-1 px-1 text-sm text-secondary hover:text-primary transition-colors"
              >
                <img src={icon_aimoge_dark1} alt="" className="h-6 w-6" />
                FANBOX
              </a>
              <a
                href="https://wiki.nijiurachan.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 items-center gap-1 px-1 text-sm text-secondary hover:text-primary transition-colors"
              >
                <img src={icon_blumoge} alt="" className="h-6 w-6" />
                Wiki
              </a>
            </nav>
          </section>

          {isSearchAvailable && (
            <>
              {/* デスクトップ: 検索バーを直接表示 */}
              <SearchBar className="hidden md:block" />
              {/* モバイル: 虫眼鏡アイコンを表示 */}
              <button
                type="button"
                onClick={toggleSearch}
                className="flex h-11 w-12 items-center justify-center rounded-full transition-colors hover:bg-muted md:hidden"
                aria-label={isSearchOpen ? "検索を閉じる" : "検索"}
              >
                {isSearchOpen ? (
                  <FiX aria-hidden="true" className="w-6 h-6" />
                ) : (
                  <FiSearch aria-hidden="true" className="w-6 h-6" />
                )}
              </button>
            </>
          )}
        </section>

        {/* モバイル: 検索バーを下に表示 */}
        {isSearchAvailable && (
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                data-testid="mobile-header-search"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="md:hidden overflow-hidden"
              >
                <SearchBar className="flex justify-center px-4 py-3" />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </header>
    </>
  );
};
